import { DataType, FormDialogMode, useNotify } from '@globaleris/ui.library'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumPeriodoLiquidacion } from '../../../api'
import { enumToSelectOptionArray } from '../../../helpers'
import {
  gridValuesGenerator,
  Period
} from '../../../helpers/gridValuesGenerator/gridValuesGenerator'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks'
import { gridRowsToDataTableRows } from './quantityAssistantModal.mapper'
import {
  QuantityAssistantDataTableRow,
  QuantityAssistantModalFormData
} from './quantityAssistantModal.model'

interface QuantityAssistantModalFormDialogProps {
  open: boolean
  onClose: (formData?: QuantityAssistantDataTableRow[]) => void
  inputData?: any
}

export const QuantityAssistantModal: React.FC<QuantityAssistantModalFormDialogProps> = ({
  open,
  onClose,
  inputData
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'quantityAssistantModal'])
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, updateFormData } =
    useFormDialog()
  const { info, error } = useNotify()

  // #region Handlers Starts
  const onFormCloseHandler = async (formData: QuantityAssistantModalFormData) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(detailAssistantDefinition?.sections!, formData)) {
        error(t('quantityAssistantModal:notifications.incompleteForm'))
        return
      }

      const startDate = moment(formData.year).startOf('year').toDate()
      const endDate = moment(formData.year).endOf('year').toDate()
      const generatedValues = gridValuesGenerator({
        startDate,
        endDate,
        period: formData.idSettlementPeriod as unknown as Period, // This works because EnumPeriodoLiquidacion and Period match in number values
        fixedQuantity: 0
      })
      const tableData = gridRowsToDataTableRows(generatedValues.value!)
      onClose(tableData)
    }

    closeForm()
    onClose()
  }
  // #endregion Handlers Ends

  // #region Form Definition Starts
  const detailAssistantDefinition = React.useMemo<FormDialogDefinitionProps>(
    () => ({
      id: 'DETAIL_ASSISTANT',
      sections: [
        {
          inputs: [
            {
              name: 'subtitleHelpText',
              labelTitle: t('quantityAssistantModal:form.inputs.subtitleHelpText'),
              type: DataType.LABEL
            },
            {
              name: 'idSettlementPeriod',
              label: t('quantityAssistantModal:form.inputs.settlementPeriod'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(
                EnumPeriodoLiquidacion,
                'enums:EnumPeriodoLiquidacion'
              ).filter(
                option =>
                  option.value !== EnumPeriodoLiquidacion.Horario &&
                  option.value !== EnumPeriodoLiquidacion.Indefinido
              ),
              required: true
            },
            {
              name: 'year',
              label: t('quantityAssistantModal:form.inputs.year'),
              type: DataType.DATE,
              views: ['year'],
              format: 'YYYY',
              defaultValue: moment(new Date()),
              onChange: (value, formState) => {
                if (moment(value).isValid()) {
                  const startDate = moment(value).startOf('year').toDate()
                  const endDate = moment(value).endOf('year').toDate()
                  updateFormData({
                    ...formState,
                    year: moment(value).toDate(),
                    startDate,
                    endDate
                  })
                }
              },
              required: true
            }
          ]
        }
      ]
    }),
    []
  )
  // #endregion Form Definition Ends

  // #region Hooks Starts
  useEffect(() => {
    open &&
      openForm({
        definition: detailAssistantDefinition,
        title: t('quantityAssistantModal:form.title'),
        okBtnLabel: t('quantityAssistantModal:actions.okButton'),
        cancelBtnLabel: t('quantityAssistantModal:actions.cancelButton'),
        mode: FormDialogMode.EDIT,
        isWaiting: false,
        onClose: onFormCloseHandler
      })
  }, [open])

  useEffect(() => {
    updateFormDefinition(detailAssistantDefinition)
  }, [detailAssistantDefinition])
  // #endregion Hooks Ends

  return (
    <>
      <CustomFormDialog {...formProps} />
    </>
  )
}
